import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppRoutes, AppRoutesCombined } from '@sparrow-card/sparrow-card-constants';
import usePortalTypeWatcher from 'hooks/usePortalTypeWatcher';
import usePrefetchFunnelChunks from 'hooks/usePrefetchFunnelChunks';
import useRouteTracker from 'hooks/useRouteTracker';
import ContentContainer from 'layout/ContentContainer';
import MainLayout from 'layout/SparrowMainLayout';
import DemoApply from 'pages/Apply/DemoApply';
import DeploymentStatusWrapper from 'pages/DeploymentStatusWrapper';
import { NotificationType } from 'pages/Notification/NotificationType';
import DirectMailOfferCode from 'pages/OfferCode/DirectMailOfferCode';
import ReinstatementOfferCode from 'pages/OfferCode/ReinstatementOfferCode';
import StarlingOfferCode from 'pages/OfferCode/StarlingOfferCode';
import DynamicRouteAccessGuard from './guards/DynamicRouteAccessGuard';
import StepChecker from './guards/StepChecker';
import {
  ApplyPage,
  DocumentVerificationPage,
  DocumentVerificationResultPage,
  NotFoundPage,
  NotificationPage,
  ReinstatementApplyPage,
  ReviewPage,
  StarlingApplyPage,
  VerifyPage,
} from './lazyPages';
import SseProcessingTimeoutLayout from './SseProcessingTimeoutLayout';
import SuspenseOutlet from './SuspenseOutlet';

const isUserCodesAdminEnabled = import.meta.env.VITE_ENVIRONMENT !== 'production';
const UserCodesAdminLazy = isUserCodesAdminEnabled
  ? React.lazy(() => import('pages/UserCodesAdmin/UserCodesAdmin'))
  : null;
const UserCodesBorrowersLazy = isUserCodesAdminEnabled
  ? React.lazy(() => import('pages/UserCodesBorrowers/UserCodesBorrowers'))
  : null;
const UserCodesApplicationsLazy = isUserCodesAdminEnabled
  ? React.lazy(() => import('pages/UserCodesApplications/UserCodesApplications'))
  : null;

const Router = () => {
  useRouteTracker();
  usePortalTypeWatcher();
  usePrefetchFunnelChunks();

  return (
    <Routes>
      {!!UserCodesAdminLazy && (
        <Route
          path="/user-codes"
          element={
            <React.Suspense fallback={null}>
              <UserCodesAdminLazy />
            </React.Suspense>
          }
        />
      )}
      {UserCodesBorrowersLazy && (
        <Route
          path="/user-codes/borrowers"
          element={
            <React.Suspense fallback={null}>
              <UserCodesBorrowersLazy />
            </React.Suspense>
          }
        />
      )}
      {UserCodesApplicationsLazy && (
        <Route
          path="/user-codes/applications"
          element={
            <React.Suspense fallback={null}>
              <UserCodesApplicationsLazy />
            </React.Suspense>
          }
        />
      )}
      <Route path="/" element={<MainLayout />}>
        <Route path="/demo/apply" element={<DemoApply />} />
        <Route path="/" element={<DeploymentStatusWrapper />}>
          <Route element={<SseProcessingTimeoutLayout />}>
            <Route path={AppRoutes.PreApproved} element={<DirectMailOfferCode />} />
            <Route
              path={AppRoutesCombined.ReinstatementPreApproved}
              element={<ReinstatementOfferCode />}
            />
            <Route path={AppRoutesCombined.StarlingPreApproved} element={<StarlingOfferCode />} />
            <Route element={<SuspenseOutlet />}>
              <Route path={AppRoutes.Apply} element={<ApplyPage />} />
              <Route
                path={AppRoutesCombined.ReinstatementApply}
                element={<ReinstatementApplyPage />}
              />
              <Route path={AppRoutesCombined.StarlingApply} element={<StarlingApplyPage />} />
              <Route element={<DynamicRouteAccessGuard />}>
                <Route
                  path={AppRoutes.OfferCodeExpired}
                  element={<NotificationPage type={NotificationType.OfferCodeExpired} />}
                />
                <Route
                  path={AppRoutes.OfferCodeUsed}
                  element={<NotificationPage type={NotificationType.OfferCodeUsed} />}
                />
                <Route
                  path={AppRoutes.PreApprovedLocked}
                  element={<NotificationPage type={NotificationType.Locked} />}
                />
                <Route
                  path={AppRoutes.AccountReopened}
                  element={<NotificationPage type={NotificationType.AccountReopened} />}
                />
                <Route
                  path={AppRoutes.AccountPermanentlyClosed}
                  element={<NotificationPage type={NotificationType.AccountPermanentlyClosed} />}
                />
              </Route>
              <Route path="/" element={<ContentContainer />}>
                <Route path="/" element={<StepChecker />}>
                  <Route
                    path={AppRoutes.Decline}
                    element={<NotificationPage type={NotificationType.Decline} />}
                  />
                  <Route
                    path={AppRoutes.Approval}
                    element={<NotificationPage type={NotificationType.Approval} />}
                  />
                  <Route
                    path={AppRoutes.Locked}
                    element={<NotificationPage type={NotificationType.Locked} />}
                  />
                  <Route path={AppRoutes.Review} element={<ReviewPage />} />
                  <Route
                    path={AppRoutes.Canceled}
                    element={<NotificationPage type={NotificationType.Canceled} />}
                  />
                  <Route path={AppRoutes.Verify} element={<VerifyPage />} />
                  <Route
                    path={AppRoutes.DocumentVerification}
                    element={<DocumentVerificationPage />}
                  />
                </Route>
                <Route
                  path={AppRoutes.DocumentVerificationResult}
                  element={<DocumentVerificationResultPage />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};
export default Router;
